// Host-facing HTTP implementation of ImageGenApi. Firebase-free: auth + base URL
// come from the injected BackendHttpDeps, so any host (PlaygroundHost, Telegram/Steam
// shells) can construct it. Do NOT import from barrel exports (imageGen/index.ts).

import { ImageGenApi, ImageGenParams, ImageGenResult, ImageGenJobEvent, DepthEstimationParams, DepthEstimationResult, RemoveBackgroundParams, RemoveBackgroundResult, UpscaleImageParams, UpscaleImageResult } from './ImageGenApi';
import { assertValidImageGenSeed } from './validateSeed';
import { resolveGameSessionToken } from '../http/backendHttpDeps';
import { RundotApiError, type ProviderErrorDetail } from '../errors';
import { mapCreditsExhaustion } from '../credits/CreditsApi';
import type { BackendHttpDeps } from '../http/backendHttpDeps';

export class HttpImageGenApi implements ImageGenApi {
  private readonly appId: string;
  private readonly deps: BackendHttpDeps;

  constructor(appId: string, deps: BackendHttpDeps) {
    this.appId = appId;
    this.deps = deps;
  }

  private async authHeaders(): Promise<Record<string, string>> {
    const idToken = await this.deps.getIdToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${idToken}`,
      'x-consumer': 'sdk',
    };

    const gst = await resolveGameSessionToken(this.deps.getGameSessionToken);
    if (gst) headers['x-h5-game-session'] = gst;

    return headers;
  }

  /**
   * Rebuild the server's structured error as a `RundotApiError` so the HTTP
   * hosts (playground, Telegram, Steam) get the same machine-readable contract
   * the RPC host already gives the app: `code` (e.g. `IMAGE_GEN_SAFETY_FILTER`,
   * `AI_POLICY_DENIED`, `CREDITS_EXHAUSTED`) and `errorDetail.type` (e.g.
   * `content_policy_violation`). A bare `Error` built from response text let
   * callers do nothing but string-match.
   */
  private async throwApiError(response: Response, fallbackMessage: string): Promise<never> {
    const errorText = await response.text();
    let errorMessage = fallbackMessage;
    let errorCode = 'UNKNOWN';
    let detail: string | undefined;
    let errorDetail: ProviderErrorDetail | undefined;
    try {
      const errorJson = JSON.parse(errorText) as Record<string, unknown>;
      errorMessage = (errorJson.error ?? errorJson.message ?? errorMessage) as string;
      if (typeof errorJson.code === 'string') errorCode = errorJson.code;
      if (typeof errorJson.detail === 'string') detail = errorJson.detail;
      if (errorJson.errorDetail && typeof errorJson.errorDetail === 'object') {
        errorDetail = errorJson.errorDetail as ProviderErrorDetail;
      }
    } catch {
      if (errorText) errorMessage = errorText;
    }
    throw new RundotApiError(errorCode, errorMessage, response.status, detail, errorDetail);
  }

  private async authedPost<T>(path: string, body: Record<string, unknown>): Promise<T> {
    const baseUrl = this.deps.getCloudRunUrl();
    const url = `${baseUrl}/v1/imagegen/${this.appId}/${path}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: await this.authHeaders(),
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      await this.throwApiError(response, `Request failed (${response.status})`);
    }

    return response.json();
  }

  async generate(params: ImageGenParams): Promise<ImageGenResult> {
    assertValidImageGenSeed(params.seed);
    return mapCreditsExhaustion(
      this.authedPost<ImageGenResult>('generate', params as unknown as Record<string, unknown>),
    );
  }

  async estimateDepth(params: DepthEstimationParams): Promise<DepthEstimationResult> {
    return mapCreditsExhaustion(
      this.authedPost<DepthEstimationResult>('estimate-depth', params as unknown as Record<string, unknown>),
    );
  }

  async removeBackground(params: RemoveBackgroundParams): Promise<RemoveBackgroundResult> {
    return mapCreditsExhaustion(
      this.authedPost<RemoveBackgroundResult>('remove-background', params as unknown as Record<string, unknown>),
    );
  }

  async upscaleImage(params: UpscaleImageParams): Promise<UpscaleImageResult> {
    return mapCreditsExhaustion(
      this.authedPost<UpscaleImageResult>('upscale-image', params as unknown as Record<string, unknown>),
    );
  }

  // `/models` is not game-scoped in its path — the optional `?gameId=` is what
  // resolves the game owner's tier. Default it to this host's app so an
  // in-game call reports the models that game can actually run, not the
  // signed-in creator's personal tier.
  async listModels(gameId?: string): Promise<readonly string[]> {
    const baseUrl = this.deps.getCloudRunUrl();
    const scope = gameId ?? this.appId;
    const url = `${baseUrl}/v1/imagegen/models?gameId=${encodeURIComponent(scope)}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: await this.authHeaders(),
    });

    if (!response.ok) {
      await this.throwApiError(response, `List image gen models failed (${response.status})`);
    }

    // The endpoint returns a bare string[], not a { models } envelope.
    return response.json();
  }

  async getCompletedJobs(): Promise<ImageGenJobEvent[]> {
    return [];
  }
}
